import { Box, MenuItem, OutlinedInput, Select, SelectChangeEvent } from "@mui/material";
import { useEffect, useState } from "react";
import ReactCountryFlag from "react-country-flag";

import { baseLanguageService, updateUser as saveUser } from "@/services";
import { dispatch, updateUser, useSelector } from "@/store";
import { Option } from "@/types";
import { handleError } from "@/utils";

const STORAGE_KEY = "s3-admin-language";
const DEFAULT_LANG = "en";

// Most ISO-639-1 language codes map cleanly to an ISO-3166 country code by
// uppercasing (es→ES, fr→FR, de→DE, ...). The exceptions get an explicit
// mapping here so the flag matches the language people expect.
const COUNTRY_BY_LANG: Record<string, string> = {
  en: "GB",
  zh: "CN",
  ja: "JP",
  ko: "KR",
  vi: "VN",
  hi: "IN",
  ar: "SA",
  he: "IL",
  uk: "UA",
  cs: "CZ",
  el: "GR",
  da: "DK",
  sv: "SE",
};

const countryForLang = (langCode: string): string => {
  if (!langCode) return "";
  const lower = langCode.toLowerCase();

  return COUNTRY_BY_LANG[lower] || lower.toUpperCase();
};

const LangFlag = ({ langCode }: { langCode: string }) => {
  const country = countryForLang(langCode);
  if (!country) return null;

  return (
    <ReactCountryFlag
      countryCode={country}
      svg
      style={{ width: "1.25em", height: "1.25em", borderRadius: 2 }}
      aria-label={country}
    />
  );
};

// Lightweight language picker - the chosen code is persisted in localStorage
// so the choice survives reloads. Once an i18n library is wired in, it can
// listen to the same key (or a `languagechange` event) to swap translations.
export default function LanguageSelector() {
  const { user } = useSelector((state) => state.auth);
  const [options, setOptions] = useState<Option[]>([]);
  const [current, setCurrent] = useState<string>(() => {
    if (user?.language) return user.language;
    if (typeof window === "undefined") return DEFAULT_LANG;

    return window.localStorage.getItem(STORAGE_KEY) || DEFAULT_LANG;
  });

  // Reflect the stored user's language if it changes (e.g. re-login).
  // `current` is intentionally omitted from deps - including it would loop
  // because this effect's whole job is to push user.language into `current`.
  useEffect(() => {
    if (user?.language && user.language !== current) {
      setCurrent(user.language);
    }
  }, [user?.language]);

  useEffect(() => {
    (async () => {
      try {
        const res = await baseLanguageService.getOptions();
        setOptions(res);
      } catch (error) {
        handleError(error);
      }
    })();
  }, []);

  const handleChange = async (event: SelectChangeEvent<string>) => {
    const next = event.target.value;
    setCurrent(next);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
      window.dispatchEvent(new CustomEvent("languagechange"));
    }
    if (!user) return;
    dispatch(updateUser({ ...user, language: next }));
    try {
      await saveUser(user._id, { language: next });
    } catch (error) {
      handleError(error);
    }
  };

  // Until the language list loads, render the cached code as a single-option
  // select so nothing flashes "-".
  const effectiveOptions: Option[] = options.length
    ? options
    : [{ label: current.toUpperCase(), value: current }];

  return (
    <Select
      value={current}
      onChange={handleChange}
      input={<OutlinedInput />}
      size="small"
      displayEmpty
      renderValue={(value) => (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
          <LangFlag langCode={String(value)} />
          {String(value).toUpperCase()}
        </Box>
      )}
      sx={{
        minWidth: 90,
        ".MuiSelect-select": { py: 0.5, display: "flex", alignItems: "center" },
      }}
      MenuProps={{ PaperProps: { sx: { maxHeight: 5 * 48 } } }}
    >
      {effectiveOptions.map((option) => (
        <MenuItem key={String(option.value)} value={String(option.value)}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
            <LangFlag langCode={String(option.value)} />
            {option.label}
          </Box>
        </MenuItem>
      ))}
    </Select>
  );
}
